import { useEffect } from 'react';
import { Routes as RouterRoutes, Route, Navigate, useLocation } from 'react-router-dom';
import { Sentry } from './lib/sentry';
import { useAuth, redirectToLogin } from './lib/auth';
import { Dashboard } from './pages/Dashboard';
import { ProjectNew } from './pages/ProjectNew';
import { ProjectDetail } from './pages/ProjectDetail';
import { MonthOverview } from './pages/MonthOverview';
import { PricelistManager } from './pages/PricelistManager';
import { PloegenManager } from './pages/PloegenManager';
import { UsersManager } from './pages/UsersManager';
import { Account } from './pages/Account';
import { ActivityLog } from './pages/ActivityLog';
import { TeDoen } from './pages/TeDoen';
import { LearnedMatches } from './pages/LearnedMatches';
import { ExtraRegelsOverzicht } from './pages/ExtraRegelsOverzicht';
import { ProjectNiveauPagina } from './pages/ProjectNiveauPagina';
import { TagsBeheer } from './pages/TagsBeheer';

// Sentry: route-patronen (/project/:id) als transactienaam i.p.v. de ruwe URL.
const Routes = Sentry.withSentryReactRouterV7Routing(RouterRoutes);

function RequireAuth({ children, adminOnly = false, beheerOnly = false, adminOfBeheer = false }: { children: React.ReactNode; adminOnly?: boolean; beheerOnly?: boolean; adminOfBeheer?: boolean }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  useEffect(() => {
    if (!loading && !user) redirectToLogin();
  }, [loading, user]);
  if (loading || !user) return <FullPageSpinner />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" replace state={{ from: location }} />;
  // Beheer (tags, standaardprijzen) volgt uit de rol (SPEC 27okt 7), niet uit admin.
  if (beheerOnly && !user.magBeheren) return <Navigate to="/" replace state={{ from: location }} />;
  // Prijslijsten en geleerde matches: beheren met de rol (Ervin, Timen), bekijken ook als admin.
  if (adminOfBeheer && user.role !== 'admin' && !user.magBeheren) return <Navigate to="/" replace state={{ from: location }} />;
  return <>{children}</>;
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-lightgrey">
      <div className="w-8 h-8 border-[3px] border-warmgrey border-t-primary rounded-full animate-spin" />
    </div>
  );
}

export function App() {
  const { loading } = useAuth();
  if (loading) return <FullPageSpinner />;

  return (
    <Routes>
      <Route path="/" element={<RequireAuth><Dashboard /></RequireAuth>} />
      <Route path="/project/new" element={<RequireAuth><ProjectNew /></RequireAuth>} />
      <Route path="/project/:id" element={<RequireAuth><ProjectDetail /></RequireAuth>} />
      <Route path="/projectnummer/:sleutel" element={<RequireAuth><ProjectNiveauPagina /></RequireAuth>} />
      <Route path="/extra-regels" element={<RequireAuth><ExtraRegelsOverzicht /></RequireAuth>} />
      <Route path="/month" element={<RequireAuth><MonthOverview /></RequireAuth>} />
      <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
      <Route path="/pricelists" element={<RequireAuth adminOfBeheer><PricelistManager /></RequireAuth>} />
      <Route path="/learned" element={<RequireAuth adminOfBeheer><LearnedMatches /></RequireAuth>} />
      <Route path="/ploegen" element={<RequireAuth adminOnly><PloegenManager /></RequireAuth>} />
      <Route path="/users" element={<RequireAuth adminOnly><UsersManager /></RequireAuth>} />
      <Route path="/tags" element={<RequireAuth beheerOnly><TagsBeheer /></RequireAuth>} />
      <Route path="/activity" element={<RequireAuth><ActivityLog /></RequireAuth>} />
      <Route path="/te-doen" element={<RequireAuth><TeDoen /></RequireAuth>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
